import { api } from "./client";

// Master kill-switch for Stripe-dependent UI flows (checkout, billing portal, plan changes).
// Set to true to re-enable subscribe/manage buttons across the app.
export const STRIPE_ENABLED = false;

export interface BillingPlan {
  id: string;
  name: string;
  display_name: string;
  interval: "monthly" | "yearly";
  metering_mode: "chats" | "messages";
  message_limit: number;
  price_cents: number | null;
  currency: string | null;
  features: string[] | null;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
}

export interface CheckoutStatusResponse {
  status: "complete" | "pending";
}

export interface PortalSessionResponse {
  portal_url: string;
}

export async function getBillingPlans(): Promise<BillingPlan[]> {
  const response = await api.get("/billing/plans");
  return response.data;
}

// Longer timeout for Stripe operations that involve external API calls
const STRIPE_TIMEOUT = 30_000;

export async function createCheckoutSession(planId: string): Promise<CheckoutSessionResponse> {
  const response = await api.post("/billing/checkout-session", { plan_id: planId }, { timeout: STRIPE_TIMEOUT });
  return response.data;
}

export async function getCheckoutStatus(sessionId: string): Promise<CheckoutStatusResponse> {
  const response = await api.get(`/billing/checkout-status/${sessionId}`, { timeout: STRIPE_TIMEOUT });
  return response.data;
}

export async function createPortalSession(): Promise<PortalSessionResponse> {
  const response = await api.post("/billing/portal-session", {}, { timeout: STRIPE_TIMEOUT });
  return response.data;
}

export interface ChangePlanResponse {
  status: string;
  new_plan_name: string;
  message: string;
}

export async function changePlan(newPlanId: string): Promise<ChangePlanResponse> {
  const response = await api.post("/billing/change-plan", { new_plan_id: newPlanId }, { timeout: STRIPE_TIMEOUT });
  return response.data;
}

export interface Invoice {
  id: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string | null;
  invoice_url: string | null;
  invoice_pdf: string | null;
  period_start: number | null;
  period_end: number | null;
  created: number;
}

export async function getInvoices(): Promise<Invoice[]> {
  const response = await api.get("/billing/invoices", { timeout: STRIPE_TIMEOUT });
  return response.data;
}
